import React from 'react'
import { Link } from 'gatsby'
import Marked from 'react-markdown'
import remarkGfm from 'remark-gfm'
import rehypeRaw from 'rehype-raw'
import Button from '../common/Button'
import CoursesToc from './CoursesToc'
import {
  ArrowRightIcon,
  BookmarkIcon,
  ClockIcon,
  TutorialIcon
} from '@atlas-co/icons'
import * as styles from './CoursesCourse.module.scss'

const calculateTotalDuration = (lessons) => {
  const total = lessons.reduce((acc, lesson) => {
    const raw =
      typeof lesson.duration === 'string'
        ? lesson.duration
        : `${lesson.duration}`
    const n = parseInt(String(raw).replace(/[^0-9]/g, ''), 10)
    return acc + (isNaN(n) ? 0 : n)
  }, 0)
  if (total >= 60) {
    const h = Math.floor(total / 60)
    const m = total % 60
    return m > 0 ? `${h}h ${m}m` : `${h}h`
  }
  return `${total} min`
}

const CoursesCourse = ({ courseData }) => {
  if (!courseData || !courseData.lessons || courseData.lessons.length === 0) {
    return (
      <div className={styles.section}>
        <div className={styles.content}>
          <h1>{courseData?.title || 'Course'}</h1>
          <p>No lessons available for this course yet.</p>
        </div>
      </div>
    )
  }

  const firstLesson = courseData.lessons[0]
  const numberOfLessons = courseData.lessons.length
  const totalDuration = calculateTotalDuration(courseData.lessons)
  const numberOfModules = new Set(
    courseData.lessons.map((l) => l.module).filter(Boolean)
  ).size

  return (
    <div className={styles.section}>
      <div className={styles.hero}>
        <div className={styles.content}>
          <div className={styles.breadcrumb}>
            <Link to="/courses/">Courses</Link>
            <span>›</span>
            <span>{courseData.title}</span>
          </div>
          <h1>{courseData.title}</h1>
          <p className={styles.description}>{courseData.description}</p>
          <div className={styles.meta}>
            {courseData.level && (
              <span className={styles.levelBadge}>{courseData.level}</span>
            )}
            <span className={styles.metaItem}>
              <BookmarkIcon size={20} /> {numberOfModules} modules
            </span>
            <span className={styles.metaItem}>
              <TutorialIcon size={20} /> {numberOfLessons} lessons
            </span>
            <span className={styles.metaItem}>
              <ClockIcon size={20} /> {totalDuration}
            </span>
            {courseData.duration && (
              <span className={styles.metaItem}>
                Suggested pace: {courseData.duration}
              </span>
            )}
          </div>
          <div className={styles.ctaRow}>
            <Button
              href={`/courses/${courseData.slug}/${firstLesson.slug}/`}
              variant="primary"
              rightIcon={<ArrowRightIcon color="#fff" />}
            >
              Start the course
            </Button>
            <Button
              href="#course-content"
              variant="secondary"
            >
              Browse lessons
            </Button>
          </div>
        </div>
      </div>

      <div className={styles.content}>
        <div className={styles.overviewCard}>
          <h2>About this course</h2>
          <div className={styles.longDescription}>
            <Marked remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>
              {courseData.longDescription || ''}
            </Marked>
          </div>
        </div>

        <div id="course-content" className={styles.tocSection}>
          <h2>Course content</h2>
          <p className={styles.tocSubtitle}>
            {numberOfModules} modules · {numberOfLessons} lessons · {totalDuration}
          </p>
          <CoursesToc
            courseSlug={courseData.slug}
            lessons={courseData.lessons}
            withBg
          />
        </div>
      </div>
    </div>
  )
}

export default CoursesCourse
